import CheckIcon from "@/assets/icons/Iconex/Light/CheckContainer.svg?react";
import CheckedIcon from "@/assets/icons/Iconex/Light/Checked.svg?react";
import type { ValidatorFn } from "@/constants/types/csv";
import type { HotRendererProps } from "@handsontable/react-wrapper";
import {
	Box,
	Checkbox,
	ClickAwayListener,
	Tooltip,
	Typography,
	styled,
} from "@mui/material";
import { type FC, type ReactNode, useState } from "react";

const TableCell = styled(Box)(() => ({
	width: "100%",
	height: "100%",
	padding: "8px 12px",
}));

type CellProps = HotRendererProps & {
	validator?: ValidatorFn;
	data: any;
};

const BaseCellRenderer: FC<CellProps & { children: ReactNode }> = ({
	value,
	validator,
	children,
	cellProperties,
	data,
	row,
}) => {
	const [open, setOpen] = useState(false);
	if (
		data[row][cellProperties.prop.toString().replace(".value", "")].disabled
	) {
		return <TableCell sx={{ backgroundColor: "grey.50" }}>{""}</TableCell>;
	}

	const result = validator ? validator(value) : true;
	const error = result === true ? null : result;
	data[row][cellProperties.prop.toString().replace(".value", "")].isValid =
		Boolean(!error);

	const handleTooltipOpen = () => {
		if (error) setOpen(true);
	};
	const handleTooltipClose = () => setOpen(false);

	return (
		<ClickAwayListener onClickAway={handleTooltipClose}>
			<Tooltip
				title={cellProperties.readOnly || !error ? "" : error}
				open={open}
				onClose={handleTooltipClose}
				placement="top"
				arrow
				disableFocusListener
				disableHoverListener
				disableTouchListener
				slotProps={{
					popper: {
						disablePortal: true,
						sx: { minWidth: "max-content" },
						modifiers: [{ name: "offset", options: { offset: [0, -20] } }],
					},
				}}
			>
				<TableCell
					sx={{
						backgroundColor: error ? "error.light" : "",
						color: error && "error.main",
					}}
					onClick={handleTooltipOpen}
				>
					{children}
				</TableCell>
			</Tooltip>
		</ClickAwayListener>
	);
};

// Text version
const TextCellRenderer: FC<CellProps> = (props) => (
	<BaseCellRenderer {...props}>
		<Typography>{props.value}</Typography>
	</BaseCellRenderer>
);

// Password version (mask with •)
const PasswordCellRenderer: FC<CellProps> = ({ value, ...rest }) => {
	const text = (value ?? "").toString();
	const masked = "*".repeat(text.length);
	return (
		<BaseCellRenderer value={value} {...rest}>
			<Typography>{masked}</Typography>
		</BaseCellRenderer>
	);
};

// Checkbox version
const CheckboxCellRenderer: FC<CellProps> = (props) => {
	const { value, row, prop, instance, cellProperties } = props;

	const checked = value === true || value === "true";

	return (
		<BaseCellRenderer {...props}>
			<Checkbox
				checked={checked}
				onClick={(e) => e.stopPropagation()}
				onChange={(_, newChecked) => {
					if (!cellProperties.readOnly) {
						instance.setDataAtRowProp(row, prop as string, newChecked);
					}
				}}
				sx={{ padding: 0 }}
				icon={<CheckIcon />}
				checkedIcon={<CheckedIcon />}
			/>
		</BaseCellRenderer>
	);
};

export const rendererMap = {
	text: TextCellRenderer,
	password: PasswordCellRenderer,
	checkbox: CheckboxCellRenderer,
	autocomplete: TextCellRenderer,
	dropdown: TextCellRenderer,
} as const;

export type RendererType = keyof typeof rendererMap;
